/**
 * Verify and migrate donation link data.
 *
 * Run from browser console after importing:
 *   import { verifyLinkData, migrateLinkData } from '@/lib/firebase/scripts/verifyLinkData';
 *   await verifyLinkData();
 *   // If satisfied with the report, run:
 *   await migrateLinkData();
 *
 * Or call from a temporary admin button.
 */
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config';

interface LinkSummary {
  platform: string;
  caseNumber: string;
  actualReceived: number;
  platformCost: number;  // old field (if present)
  hasActualReceived: boolean;
  hasPlatformCost: boolean;
  holderDocId: string;
  entryIndex: number;
}

interface CaseEntrySummary {
  caseDocId: string;
  platform: string;
  caseNumber: string;
  amount: number;
  platformCost: number;
  hasAmount: boolean;
  hasPlatformCost: boolean;
  entryIndex: number;
}

export async function verifyLinkData() {
  console.log('=== LINK DATA VERIFICATION ===\n');

  const snapshot = await getDocs(collection(db, 'treatmentSupportRequests'));

  const holderLinks: LinkSummary[] = [];
  const caseEntries: CaseEntrySummary[] = [];
  let holderCount = 0;
  let caseCount = 0;

  snapshot.forEach(docSnap => {
    const data = docSnap.data();
    const isHolder = !!data.isLinkHolder;

    if (isHolder) {
      holderCount++;
      (data.donationEntries || []).forEach((entry: any, idx: number) => {
        holderLinks.push({
          platform: entry.platform || '?',
          caseNumber: entry.caseNumber || '?',
          actualReceived: entry.actualReceived || 0,
          platformCost: entry.platformCost || 0,
          hasActualReceived: 'actualReceived' in entry && entry.actualReceived != null,
          hasPlatformCost: 'platformCost' in entry && entry.platformCost != null,
          holderDocId: docSnap.id,
          entryIndex: idx,
        });
      });
    } else {
      caseCount++;
      (data.donationEntries || []).forEach((entry: any, idx: number) => {
        if (entry.platform && entry.caseNumber) {
          caseEntries.push({
            caseDocId: docSnap.id,
            platform: entry.platform,
            caseNumber: entry.caseNumber,
            amount: entry.amount || 0,
            platformCost: entry.platformCost || 0,
            hasAmount: 'amount' in entry && entry.amount != null,
            hasPlatformCost: 'platformCost' in entry && entry.platformCost != null,
            entryIndex: idx,
          });
        }
      });
    }
  });

  // Holder link stats
  const totalReceived = holderLinks.reduce((s, l) => s + (l.actualReceived || l.platformCost || 0), 0);
  const totalActualReceived = holderLinks.reduce((s, l) => s + l.actualReceived, 0);
  const totalPlatformCost = holderLinks.reduce((s, l) => s + l.platformCost, 0);
  const missingActualReceived = holderLinks.filter(l => !l.hasActualReceived && l.hasPlatformCost);
  const hasNeither = holderLinks.filter(l => !l.hasActualReceived && !l.hasPlatformCost);

  // Case entry stats
  const totalAllocated = caseEntries.reduce((s, e) => s + (e.amount || e.platformCost || 0), 0);
  const missingAmount = caseEntries.filter(e => !e.hasAmount && e.hasPlatformCost);
  const caseHasNeither = caseEntries.filter(e => !e.hasAmount && !e.hasPlatformCost);

  // Platform breakdown
  const byPlatform: Record<string, { count: number; total: number }> = {};
  for (const l of holderLinks) {
    const p = l.platform;
    if (!byPlatform[p]) byPlatform[p] = { count: 0, total: 0 };
    byPlatform[p].count++;
    byPlatform[p].total += l.actualReceived || l.platformCost || 0;
  }

  console.log(`Documents: ${snapshot.size} total (${holderCount} holders, ${caseCount} cases)`);
  console.log(`\n--- HOLDER LINKS (${holderLinks.length} entries) ---`);
  console.log(`Total (actualReceived || platformCost): ${totalReceived.toLocaleString()} SAR`);
  console.log(`  Sum actualReceived fields: ${totalActualReceived.toLocaleString()}`);
  console.log(`  Sum platformCost fields:   ${totalPlatformCost.toLocaleString()}`);
  console.log(`  Missing actualReceived (has platformCost): ${missingActualReceived.length}`);
  console.log(`  Has neither field: ${hasNeither.length}`);

  console.log(`\nBy platform:`);
  for (const [p, data] of Object.entries(byPlatform)) {
    console.log(`  ${p}: ${data.count} links, ${data.total.toLocaleString()} SAR`);
  }

  console.log(`\n--- CASE ENTRIES (${caseEntries.length} entries) ---`);
  console.log(`Total allocated (amount || platformCost): ${totalAllocated.toLocaleString()} SAR`);
  console.log(`  Missing amount (has platformCost): ${missingAmount.length}`);
  console.log(`  Has neither: ${caseHasNeither.length}`);

  console.log(`\n--- EXCEL COMPARISON ---`);
  console.log(`Excel reference: 1,644,151.28 SAR across 147 links`);
  console.log(`Firestore:       ${totalReceived.toLocaleString()} SAR across ${holderLinks.length} links`);
  console.log(`Difference:      ${(totalReceived - 1644151.28).toLocaleString()} SAR`);
  console.log(`Link count diff: ${holderLinks.length - 147}`);

  if (missingActualReceived.length > 0) {
    console.log(`\n--- NEEDS MIGRATION (${missingActualReceived.length} holder entries) ---`);
    for (const l of missingActualReceived.slice(0, 10)) {
      console.log(`  ${l.platform}/${l.caseNumber}: platformCost=${l.platformCost} → actualReceived (doc: ${l.holderDocId})`);
    }
    if (missingActualReceived.length > 10) {
      console.log(`  ... and ${missingActualReceived.length - 10} more`);
    }
  }

  return {
    holderLinks,
    caseEntries,
    totalReceived,
    missingActualReceived,
    missingAmount,
    byPlatform,
  };
}

/**
 * Migrate old data:
 * 1. Holder entries: copy platformCost → actualReceived (if missing), strip platformCost
 * 2. Case entries: copy platformCost → amount (if missing), strip platformCost
 */
export async function migrateLinkData(dryRun = true) {
  console.log(`\n=== LINK DATA MIGRATION (${dryRun ? 'DRY RUN' : 'LIVE'}) ===\n`);

  const snapshot = await getDocs(collection(db, 'treatmentSupportRequests'));
  let updatedDocs = 0;
  let updatedEntries = 0;

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    const entries = data.donationEntries;
    if (!entries || entries.length === 0) continue;

    const isHolder = !!data.isLinkHolder;
    let changed = false;

    const newEntries = entries.map((entry: any) => {
      const updated = { ...entry };

      if (isHolder) {
        // Holder: ensure actualReceived exists
        if (!('actualReceived' in updated) || updated.actualReceived == null) {
          if (updated.platformCost != null) {
            updated.actualReceived = updated.platformCost;
            changed = true;
            updatedEntries++;
          }
        }
      } else {
        // Case entry: ensure amount exists
        if (!('amount' in updated) || updated.amount == null) {
          if (updated.platformCost != null) {
            updated.amount = updated.platformCost;
            changed = true;
            updatedEntries++;
          }
        }
      }

      // Strip platformCost
      if ('platformCost' in updated) {
        delete updated.platformCost;
        changed = true;
      }

      return updated;
    });

    if (changed) {
      updatedDocs++;
      if (!dryRun) {
        await updateDoc(doc(db, 'treatmentSupportRequests', docSnap.id), {
          donationEntries: newEntries,
        });
      }
      console.log(`${dryRun ? '[DRY]' : '[DONE]'} ${docSnap.id} (${isHolder ? 'holder' : 'case'}): ${newEntries.length} entries`);
    }
  }

  console.log(`\nSummary: ${updatedDocs} docs, ${updatedEntries} entries ${dryRun ? 'would be' : 'were'} updated`);
  if (dryRun) {
    console.log('Run migrateLinkData(false) to apply changes.');
  }

  return { updatedDocs, updatedEntries };
}
